import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import {
  admin,
  type AuditLogRow,
  type AuditLogFilters,
} from "@/lib/admin";
import { AdminLayout } from "@/components/AdminLayout";
import { Card } from "@/components/Card";
import { LoadingScreen } from "@/components/LoadingScreen";
import { Alert } from "@/components/Alert";
import { Button } from "@/components/Button";
import { useAdminRole, canManageAdmins } from "@/hooks/useAdminRole";

// ────────────────────────────────────────────────────────────────────────
// Audit log — super_admin only. Surfaces every privileged action across
// the system (admin resets, refunds, role grants, patient cancel events,
// refill decisions, etc.) from public.access_log.
//
// Every meaningful view of this page (filtered or paginated) is itself
// logged with action = "view_audit_log" so we keep a meta-trail.
// ────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function fmtAbsolute(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// Friendly title for known action strings; falls back to the raw value.
function actionLabel(action: string): string {
  const map: Record<string, string> = {
    reset_password:                  "Sent password reset",
    send_magic_link:                 "Sent magic-link",
    update_email:                    "Changed user email",
    archive_user:                    "Archived user",
    unarchive_user:                  "Unarchived user",
    set_admin_role:                  "Set admin role",
    remove_admin_role:               "Removed admin role",
    delete_user:                     "Deleted user",
    create_coupon:                   "Created coupon",
    update_coupon:                   "Updated coupon",
    delete_coupon:                   "Deleted coupon",
    admin_role_granted:              "Granted admin role",
    admin_role_granted_with_invite:  "Invited + granted admin",
    refill_decided:                  "Decided refill request",
    refund_issued:                   "Issued refund",
    subscription_retention_applied:  "Patient took retention offer",
    subscription_cancelled:          "Patient cancelled subscription",
    cancel_flow_exit:                "Patient abandoned cancel flow",
    trt_subscription_link_sent:      "Sent TRT subscription link",
    patient_link_stripe:             "Patient linked Stripe",
    billing_portal_session_created:  "Opened billing portal",
    view_audit_log:                  "Viewed audit log",
  };
  return map[action] ?? action;
}

const ROLE_BADGE: Record<string, string> = {
  super_admin: "bg-fh-green/15 text-fh-green-dark border-fh-green/30",
  admin:       "bg-fh-sand text-fh-ink border-fh-border",
  viewer:      "bg-fh-cream text-fh-gray border-fh-border",
  patient:     "bg-white text-fh-gray border-fh-border",
};

export default function AdminAuditLog() {
  const { role, loading } = useAdminRole();
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const auditQ = useQuery({
    queryKey: ["admin", "auditLog", filters, offset],
    queryFn: () => admin.getAuditLog(filters, { limit: PAGE_SIZE, offset }),
    enabled: canManageAdmins(role),
    staleTime: 15_000,
  });

  if (loading) return <AdminLayout><LoadingScreen /></AdminLayout>;
  if (!canManageAdmins(role)) {
    // Same bounce pattern as AdminAdmins — non-super_admins shouldn't see this.
    return <Navigate to="/admin/users" replace />;
  }

  const data = auditQ.data;
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const distinct = data?.distinct ?? { actions: [], tables: [], roles: [] };

  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + PAGE_SIZE, total);

  function updateFilter<K extends keyof AuditLogFilters>(key: K, value: AuditLogFilters[K]) {
    setFilters((prev) => {
      const next = { ...prev };
      if (value === "" || value == null) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
    setOffset(0); // reset to page 1 on any filter change
  }

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function resetFilters() {
    setFilters({});
    setOffset(0);
  }

  const hasAnyFilter = Object.keys(filters).length > 0;

  return (
    <AdminLayout>
      <header className="mb-5">
        <h1 className="font-serif text-[28px] text-fh-ink leading-tight mb-1">Audit log</h1>
        <p className="text-[14px] text-fh-gray m-0">
          Every privileged action across the platform. Filter by actor, action, or date.
          Your views of this page are themselves recorded (action <code className="text-[12px]">view_audit_log</code>).
        </p>
      </header>

      {auditQ.error && (
        <Alert tone="error">{(auditQ.error as Error).message}</Alert>
      )}

      <Card title="Filters" action={hasAnyFilter ? (
        <button
          type="button"
          onClick={resetFilters}
          className="text-[12px] font-semibold text-fh-gray hover:text-fh-ink"
        >
          Clear all
        </button>
      ) : undefined}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <FilterSelect
            label="Action"
            value={filters.action ?? ""}
            options={distinct.actions}
            onChange={(v) => updateFilter("action", v)}
            placeholder="All actions"
          />
          <FilterSelect
            label="Actor role"
            value={filters.actor_role ?? ""}
            options={distinct.roles}
            onChange={(v) => updateFilter("actor_role", v)}
            placeholder="All roles"
          />
          <FilterSelect
            label="Table"
            value={filters.table_name ?? ""}
            options={distinct.tables}
            onChange={(v) => updateFilter("table_name", v)}
            placeholder="All tables"
          />
          <FilterInput
            label="From"
            type="datetime-local"
            value={isoToLocal(filters.date_from)}
            onChange={(v) => updateFilter("date_from", localToIso(v))}
          />
          <FilterInput
            label="To"
            type="datetime-local"
            value={isoToLocal(filters.date_to)}
            onChange={(v) => updateFilter("date_to", localToIso(v))}
          />
          <FilterInput
            label="Search (action / table)"
            type="search"
            placeholder="e.g. refund, archive"
            value={filters.search ?? ""}
            onChange={(v) => updateFilter("search", v)}
          />
        </div>
      </Card>

      <div className="mt-4">
        <Card
          title={`${total.toLocaleString()} event${total === 1 ? "" : "s"}`}
          action={auditQ.isFetching ? "Refreshing…" : `${start}–${end} of ${total.toLocaleString()}`}
        >
          {rows.length === 0 ? (
            <p className="text-[13px] text-fh-gray italic m-0 py-3">
              {auditQ.isLoading ? "Loading…" : "No events match those filters."}
            </p>
          ) : (
            <div className="overflow-x-auto -mx-[22px] -my-[10px]">
              <table className="w-full text-[12.5px]">
                <thead className="text-left text-[11px] uppercase tracking-wider text-fh-gray bg-fh-cream border-b border-fh-border">
                  <tr>
                    <th className="px-3 py-2.5 font-semibold w-[140px]">When</th>
                    <th className="px-3 py-2.5 font-semibold w-[220px]">Actor</th>
                    <th className="px-3 py-2.5 font-semibold">Action</th>
                    <th className="px-3 py-2.5 font-semibold w-[180px]">Target</th>
                    <th className="px-3 py-2.5 font-semibold text-right w-[70px]">Context</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <Row key={r.id} row={r} expanded={expanded.has(r.id)} onToggle={() => toggleExpand(r.id)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-fh-border">
              <div className="text-[12px] text-fh-gray">
                Page {Math.floor(offset / PAGE_SIZE) + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={offset === 0 || auditQ.isFetching}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  ← Newer
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={end >= total || auditQ.isFetching}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Older →
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      <p className="text-center text-[11.5px] text-fh-gray mt-6">
        Distinct action / table / role lists are sampled from the last 90 days.
        For older filters, type a value directly into the Search box.
      </p>
    </AdminLayout>
  );
}

// ────────────────────────────────────────────────────────────────────────

function Row({
  row, expanded, onToggle,
}: { row: AuditLogRow; expanded: boolean; onToggle: () => void }) {
  const hasContext = row.context && Object.keys(row.context).length > 0;
  const roleBadge = row.actor_role ? ROLE_BADGE[row.actor_role] ?? ROLE_BADGE.viewer : ROLE_BADGE.viewer;
  const target = row.record_id ?? row.patient_id ?? "—";
  return (
    <>
      <tr className="border-b border-fh-border last:border-0 hover:bg-fh-cream/40 align-top">
        <td className="px-3 py-2.5 text-fh-ink whitespace-nowrap" title={fmtAbsolute(row.occurred_at)}>
          {relTime(row.occurred_at)}
        </td>
        <td className="px-3 py-2.5 text-fh-ink">
          <div className="flex flex-col gap-0.5">
            <span className="truncate max-w-[200px]" title={row.actor_email ?? row.actor_id ?? ""}>
              {row.actor_email ?? (row.actor_id ? row.actor_id.slice(0, 8) + "…" : "system")}
            </span>
            {row.actor_role && (
              <span className={`inline-block w-fit text-[10px] uppercase tracking-wider border rounded px-1.5 py-0.5 ${roleBadge}`}>
                {row.actor_role.replace("_", " ")}
              </span>
            )}
          </div>
        </td>
        <td className="px-3 py-2.5 text-fh-ink">
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold">{actionLabel(row.action)}</span>
            <code className="text-[10.5px] text-fh-gray">{row.action}</code>
          </div>
        </td>
        <td className="px-3 py-2.5 text-fh-gray font-mono text-[11.5px] break-all">
          {row.table_name && <div className="text-fh-ink">{row.table_name}</div>}
          <div className="truncate max-w-[170px]" title={target}>{target}</div>
        </td>
        <td className="px-3 py-2.5 text-right">
          {hasContext ? (
            <button
              type="button"
              onClick={onToggle}
              className="text-[12px] font-semibold text-fh-green hover:text-fh-green-dark"
              aria-expanded={expanded}
            >
              {expanded ? "Hide" : "View"}
            </button>
          ) : (
            <span className="text-[11.5px] text-fh-gray-light">—</span>
          )}
        </td>
      </tr>
      {expanded && hasContext && (
        <tr className="border-b border-fh-border last:border-0 bg-fh-cream/40">
          <td colSpan={5} className="px-3 py-3">
            <pre className="text-[11.5px] bg-fh-ink text-fh-cream rounded-[8px] p-3 overflow-auto whitespace-pre-wrap break-all m-0">
{JSON.stringify(row.context, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function FilterSelect({
  label, value, options, onChange, placeholder,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wider text-fh-gray font-semibold">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-[13px] bg-white border border-fh-border rounded-[8px] px-2.5 py-1.5 text-fh-ink focus:outline-none focus:border-fh-green focus:ring-2 focus:ring-fh-green/20"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>{actionLabel(o)}</option>
        ))}
      </select>
    </label>
  );
}

function FilterInput({
  label, type, value, onChange, placeholder,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wider text-fh-gray font-semibold">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="text-[13px] bg-white border border-fh-border rounded-[8px] px-2.5 py-1.5 text-fh-ink focus:outline-none focus:border-fh-green focus:ring-2 focus:ring-fh-green/20"
      />
    </label>
  );
}

// `<input type="datetime-local">` wants "YYYY-MM-DDTHH:mm" in local time;
// we store filters as ISO strings. These bridge the two formats.
function isoToLocal(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localToIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

